import React from 'react';
import styled from 'styled-components';
import { Body5, Checkbox, Container } from '../../libs/nvstr-common-ui.es';
import { DocSentimentChart } from '../charts/DocSentimentChart';
import { FlatButton } from '../../components/buttons';
import { SkeletonButton } from '../../components/buttons/SkeletonButton';
import { SpeakerDocSentimentChart } from '../charts/SpeakerDocSentimentChart';
import { useFedSentiment } from '../../hooks/features/useFedSentiment';
import { DocSentimentTimeChart } from '../charts/DocSentimentTimeChart';
import { SpeakerByDocSentimentTimeChart } from '../charts/SpeakerByDocSentimentTimeChart';

const CHART_KEYS = {
  MEAN: 'mean',
  STD_DEVIATION: 'std_dev',
  MIN: 'min',
  MAX: 'max',
};

const Wrapper = styled.div``;
const KeySelectButtonWrapper = styled.div`
  button {
    width: 148px;
    padding: 8px 10px;

    * {
      font-size: 10px !important;
    }
  }
`;
const ValueSelectorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const valueDisplay = (value) => {
  switch (value) {
    case CHART_KEYS.MEAN: {
      return 'Mean';
    }
    case CHART_KEYS.STD_DEVIATION: {
      return 'Standard Deviation';
    }
    default: {
      return value;
    }
  }
};

const KeySelectButton = ({ onClick, value, isActive }) => {
  const hc = () => {
    onClick(value);
  };
  if (isActive) {
    return (
      <KeySelectButtonWrapper>
        <FlatButton onClick={() => null}>{valueDisplay(value)}</FlatButton>
      </KeySelectButtonWrapper>
    );
  }
  return (
    <KeySelectButtonWrapper>
      <SkeletonButton onClick={hc}>{valueDisplay(value)}</SkeletonButton>
    </KeySelectButtonWrapper>
  );
};

const ValueSelector = ({ onClick, activeValue, keys }) => {
  return (
    <ValueSelectorWrapper>
      {keys.map((k) => (
        <KeySelectButton key={k} isActive={k === activeValue} value={k} onClick={onClick} />
      ))}
    </ValueSelectorWrapper>
  );
};

const DocAggregation = ({ timeOnXAxis }) => {
  const { docAggregationChartData: formattedData, docAggregationTimeXAxisChartData: timeXAxisFormattedData } =
    useFedSentiment();

  const [keys] = React.useState([CHART_KEYS.MEAN, CHART_KEYS.STD_DEVIATION]);
  const [activeKey, setActiveKey] = React.useState(CHART_KEYS.MEAN);

  if (!formattedData) return null;

  const chartData = timeOnXAxis ? timeXAxisFormattedData[activeKey] : formattedData[activeKey];

  const onKeyChange = (v) => setActiveKey(v);

  if (!chartData) {
    return null;
  }
  return (
    <Wrapper>
      {timeOnXAxis ? (
        <DocSentimentTimeChart data={chartData} activeKey={valueDisplay(activeKey)} />
      ) : (
        <DocSentimentChart data={chartData} activeKey={valueDisplay(activeKey)} />
      )}
      <Container top={24} left={24} row spaceBetween verticallyCentered>
        <ValueSelector activeValue={activeKey} onClick={onKeyChange} keys={keys} />
      </Container>
    </Wrapper>
  );
};

const SpeakerByDocAggregation = ({ timeOnXAxis }) => {
  const {
    speakerByDocAggregationChartData: formattedData,
    speakerByDocAggregationTimeXAxisChartData: timeXAxisFormattedData,
  } = useFedSentiment();

  const [keys] = React.useState([CHART_KEYS.MEAN, CHART_KEYS.STD_DEVIATION]);
  const [activeKey, setActiveKey] = React.useState(CHART_KEYS.MEAN);

  if (!formattedData) return null;

  const chartData = timeOnXAxis ? timeXAxisFormattedData[activeKey] : formattedData[activeKey];

  const onKeyChange = (v) => setActiveKey(v);

  if (!chartData) {
    return null;
  }

  return (
    <Wrapper>
      {timeOnXAxis ? (
        <SpeakerByDocSentimentTimeChart data={chartData} activeKey={valueDisplay(activeKey)} />
      ) : (
        <SpeakerDocSentimentChart data={chartData} activeKey={valueDisplay(activeKey)} />
      )}
      <Container top={24} left={24}>
        <ValueSelector activeValue={activeKey} onClick={onKeyChange} keys={keys} />
      </Container>
    </Wrapper>
  );
};

export const SentimentAggregations = () => {
  const [timeOnXAxis, setTimeOnXAxis] = React.useState(false);

  return (
    <Container>
      <Container row spaceBetween verticallyCentered>
        <div />
        <Container row verticallyCentered bottom={8}>
          <Checkbox
            name={'show-doc-senti-timelabels'}
            value={timeOnXAxis}
            onChange={timeOnXAxis ? () => () => setTimeOnXAxis(false) : () => () => setTimeOnXAxis(true)}
          />
          <label htmlFor={'show-doc-senti-timelabels'}>
            <Body5>Use Dates As Axis</Body5>
          </label>
        </Container>
      </Container>
      <DocAggregation timeOnXAxis={timeOnXAxis} />
      <Container top={24}>
        <SpeakerByDocAggregation timeOnXAxis={timeOnXAxis} />
      </Container>
    </Container>
  );
};
